/* ============================================================
   IDEAS CAFE — <ideas-menu-nav>

   The sticky section rail on the menu page. Two jobs:

     · mark the chip for whichever menu section is currently in view
     · keep that chip scrolled into view inside the rail, so the
       marker is never hidden off the edge on a 13-section menu

   Uses IntersectionObserver rather than a scroll handler, and a
   negative top margin so a section counts as "current" once it
   reaches the area just below the sticky rail — not when its very
   first pixel enters the viewport.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('ideas-menu-nav')) return;

  class IdeasMenuNav extends HTMLElement {
    connectedCallback() {
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-spy]'));
      if (!this.chips.length) return;

      this.rail = this.querySelector('.ic-chip-rail');
      this.byId = {};

      var targets = [];
      var self = this;

      this.chips.forEach(function (chip) {
        var id = chip.dataset.spy;
        var el = document.getElementById(id);
        if (!el) return;
        self.byId[id] = chip;
        targets.push(el);
      });

      if (!targets.length) return;

      this._bindSmoothScroll();
      this._observe(targets);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    _bindSmoothScroll() {
      var self = this;
      this.chips.forEach(function (chip) {
        chip.addEventListener('click', function (event) {
          var id = chip.dataset.spy;
          var target = document.getElementById(id);
          if (!target) return;

          event.preventDefault();

          // Offset by the rail's own height so the heading is not
          // left sitting underneath it.
          var offset = self.getBoundingClientRect().height || 0;
          var top = target.getBoundingClientRect().top + window.pageYOffset - offset - 8;

          window.scrollTo({
            top: top,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth'
          });

          // Reflect immediately; the observer would otherwise lag the
          // scroll animation.
          self._mark(id);
          history.replaceState({}, '', '#' + id);
        });
      });
    }

    _observe(targets) {
      var self = this;
      var visible = new Map();

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) visible.set(entry.target.id, entry.intersectionRatio);
            else visible.delete(entry.target.id);
          });

          if (!visible.size) return;

          // Whichever visible section occupies the most of the band
          // wins, so a short section sandwiched between two long ones
          // does not flicker the marker.
          var best = null;
          var bestRatio = -1;
          visible.forEach(function (ratio, id) {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              best = id;
            }
          });

          if (best) self._mark(best);
        },
        {
          // Band sits just below the sticky rail.
          rootMargin: '-120px 0px -55% 0px',
          threshold: [0, 0.25, 0.5, 0.75, 1]
        }
      );

      targets.forEach(function (t) {
        self.observer.observe(t);
      });
    }

    _mark(id) {
      if (this._current === id) return;
      this._current = id;

      var self = this;
      this.chips.forEach(function (chip) {
        var on = chip.dataset.spy === id;
        chip.classList.toggle('ic-chip--active', on);
        if (on) {
          chip.setAttribute('aria-current', 'true');
          self._reveal(chip);
        } else {
          chip.removeAttribute('aria-current');
        }
      });
    }

    // Scroll the active chip into view within the rail only — never
    // the page, which would fight the user's own scrolling.
    _reveal(chip) {
      if (!this.rail) return;

      var railBox = this.rail.getBoundingClientRect();
      var chipBox = chip.getBoundingClientRect();

      if (chipBox.left < railBox.left || chipBox.right > railBox.right) {
        this.rail.scrollTo({
          left: chip.offsetLeft - this.rail.clientWidth / 2 + chip.offsetWidth / 2,
          behavior: 'smooth'
        });
      }
    }
  }

  customElements.define('ideas-menu-nav', IdeasMenuNav);
})();
